import { Button, Screen } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents/CenteredSpinner';
import { BackButton } from '~/commonComponents';
import { TextAreaField } from '~/components/shared/forms';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { GET_MESSAGES_BY_JOB_ID, getMessagesByJobId, postMessage } from '../../actions';
import { createLoadingSelector } from '../../api/selectors';
import { CustomPortal } from '../../commonComponents/CustomPortal/CustomPortal';
import { colors } from '@tempmee/tailwind';
import { Colors } from '../../themes/colors';
import history from '../../browserHistory';
import chatSendButton from '../../images/chatSendButton.png';
import '../../themes/confirm_hyg.scss';
import '../../themes/global.scss';
import ChatHeader from './ChatHeader';
import GuidelinesPortal from './GuidelinesPortal';
import {
  renderAdminMessage,
  renderOpponentMessage,
  renderSupportCard,
  renderUserMessage,
} from './RenderFunc';
import { NumberUtils } from '~/utils/NumberUtils';
import { useAppSelector } from '~/hooks/useAppSelector';

export default function Chat() {
  const params = useParams() as { jobId?: string } | undefined;
  const messageRef = useRef<HTMLDivElement>(null);

  const [newMessage, setMessage] = useState<string>('');
  const [isGuidelineOpen, setGuidelineOpen] = useState<boolean>(false);
  const [isSupportPopupOpen, setSupportPopupOpen] = useState<boolean>(false);

  const jobId = history.location.state ? (history.location.state as { jobId?: string }).jobId : params?.jobId;
  const user = useAppSelector((state) => state.user.user);
  const currentUserId = user?.id ?? '';
  const selectedJob = useAppSelector((state) => {
    if (state.job.job?.id === jobId) {
      return state.job.job;
    }

    return null;
  });

  const messagedPerJob = useAppSelector((state) => state.messages.messagedPerJob);

  const actions = [GET_MESSAGES_BY_JOB_ID];
  const loadingSelector = createLoadingSelector(actions);
  const isLoading = useAppSelector((state) => loadingSelector(state));
  const [prevIsLoading, setPrevIsLoading] = useState<boolean>(isLoading);

  const isPostMessageEnabled = moment(selectedJob?.local_date).diff(moment(), 'days') >= 0;

  const dispatch = useDispatch();

  const scrollToBottomMessages = () =>
    messageRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' });

  const disableRequest = () => !isPostMessageEnabled;

  useEffect(() => {
    dispatch(getMessagesByJobId({ jobId }));

    if (selectedJob?.id === jobId && messagedPerJob.length === 0) {
      setGuidelineOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && prevIsLoading && isPostMessageEnabled) {
      scrollToBottomMessages();
    }

    setPrevIsLoading(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, messagedPerJob]);

  const renderPostMessageView = () => {
    if (!isLoading && isPostMessageEnabled) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '90%',
            alignSelf: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 50,
          }}
        >
          <div style={{ flex: 1 }}>
            <TextAreaField
              placeholder="Enter your message"
              height={80}
              value={newMessage}
              onChange={(e) => setMessage(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <img
            src={chatSendButton}
            style={{
              width: 50,
              height: 50,
              marginLeft: 30,
              cursor: 'pointer',
            }}
            alt="Send"
            onClick={() => {
              setMessage('');
              dispatch(postMessage({ jobId: selectedJob.id, message: newMessage.trim() }));
            }}
          />
        </div>
      );
    }
    return <div />;
  };
  const renderMessages = () => (
    <div
      style={{
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {messagedPerJob.map((messageEl) => {
        if (messageEl.type === 'ADMIN-INVITE') {
          return renderSupportCard();
        }
        if (messageEl.type === 'WARNING') {
          return renderSupportCard(messageEl.text, messageEl.id);
        }
        if (messageEl.author?.isAdmin && messageEl.author?.id !== currentUserId) {
          return renderAdminMessage(
            messageEl.text,
            moment(messageEl.created_at).fromNow(),
            messageEl.isLast,
            messageEl.id,
          );
        }
        if (messageEl.author?.id === currentUserId && messageEl.type === 'MESSAGE') {
          return renderUserMessage(
            messageEl.text,
            moment(messageEl.created_at).fromNow(),
            messageEl.isLast,
            messageEl.id,
          );
        }

        return renderOpponentMessage(
          messageEl.text,
          moment(messageEl.created_at).fromNow(),
          messageEl.isLast,
          messageEl.id,
        );
      })}
      <div style={{ height: 20 }} ref={messageRef} />
    </div>
  );

  return (
    <Screen variant="top-card">
      <Screen.Header
        start={<BackButton color={colors.shades[0]} />}
        title={
          <ChatHeader
            userInfo={selectedJob?.offer_final_reciever}
            jobDate={selectedJob?.local_date}
            isOnsiteInterview={selectedJob?.isOnsiteInterview}
          />
        }
        end={
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {/* Equal fixed width so the two stacked actions match (legacy paired size="small" = 200px). */}
            <Button
              variant="outlined"
              size="md"
              onClick={() => setGuidelineOpen(true)}
              style={{ width: 200 }}
            >
              Chat Guidelines
            </Button>
            {/* No DS "success" variant exists; tint the light variant green (stays green on
                hover, unlike a primary + backgroundColor override). */}
            <Button
              variant="light"
              size="md"
              disabled={disableRequest()}
              className={disableRequest() ? undefined : 'bg-success-500 text-shades-0'}
              onClick={() => setSupportPopupOpen(true)}
              style={{ width: 200 }}
            >
              Request Support
            </Button>
          </div>
        }
        style={{
          backgroundColor: Colors.primary_500,
          boxShadow: '0px 3px 4px 0px rgba(0,0,0,0.2) inset',
        }}
      >
        {/* Spacer: the title row is a fixed 44px, but the centered avatar and the
            stacked end buttons are taller and are absolutely positioned on that row.
            This empty child grows the header so they sit inside the teal instead of
            bleeding onto the body below. */}
        <div style={{ height: 14 }} />
      </Screen.Header>
      <Screen.Body>
        <div style={{ width: '100%', minHeight: 500 }} className="chat_component">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '70%' }}>
              {isLoading ? <CenteredSpinner /> : renderMessages()}
            </div>
          </div>
        </div>
        {renderPostMessageView()}

        {isGuidelineOpen ? (
          <GuidelinesPortal gotItClicked={() => setGuidelineOpen(false)} />
        ) : (
          <div />
        )}

        {isSupportPopupOpen && (
          <CustomPortal
            title="Request Support"
            // eslint-disable-next-line max-len
            text={`Need immediate support? Send us a text at ${NumberUtils.getCSPhone().formatted} and we'll assist you as quickly as possible.`}
            submitPressed={() => setSupportPopupOpen(false)}
            buttonText="Done"
            redirect={false}
          />
        )}
      </Screen.Body>
    </Screen>
  );
}
